import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Sun, Moon, LogIn, ArrowRight, Sparkles, AlertCircle, Clock, Building2, Target,
  FileText, Handshake, Network, BarChart3, MessageSquareText, Keyboard, ShieldCheck, Lock, ScrollText,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import BrandMark from '../components/BrandMark';
import CaptureDemo from './landing/CaptureDemo';
import RequestAccessForm from './landing/RequestAccessForm';
import { TODAY_SAMPLE, REVIEW_SAMPLE } from './landing/demoSamples';
import './Landing.css';

// The public front door: what a signed-out visitor sees at /. It explains the
// product, lets them try AI capture on sample notes, and takes a request for
// access. Everything shown is sample data; nothing here calls a signed-in API.

const TODAY_ICONS = { Overdue: AlertCircle, 'Due today': Clock, 'Quiet accounts': Building2, 'Past close date': Target };

const FEATURES = [
  { icon: FileText, title: 'Quotes and orders', text: 'Build quotes with line items, send a branded PDF, and carry the win through to the order.' },
  { icon: Handshake, title: 'Rep firms', text: 'Keep the rep firms and the people who cover each account right next to the deal.' },
  { icon: Network, title: 'Influence and account maps', text: 'See who influences whom at an account, and how accounts roll up to their parents.' },
  { icon: BarChart3, title: 'Reports and forecast', text: 'Pipeline, forecast and revenue at a glance, plus a report builder for the rest.' },
  { icon: MessageSquareText, title: 'An assistant that asks first', text: 'Ask about any account. It reads with your permissions and proposes changes for your yes.' },
  { icon: Keyboard, title: 'Fast for daily use', text: 'A command palette, keyboard shortcuts and edits right in the row.' },
];

const PRINCIPLES = [
  { icon: ShieldCheck, title: 'Nothing saves without your yes', text: 'Every AI draft is a proposal. Records are written only when you approve them.' },
  { icon: Lock, title: 'Everyone sees what they should', text: 'Roles, modules and per-record access decide who sees and edits each account.' },
  { icon: ScrollText, title: 'Every AI call is on the record', text: 'Admins see every AI request and its token use, and each person has a daily limit.' },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function Landing() {
  const { theme, toggleTheme } = useTheme();
  const { hash } = useLocation();
  const [audience, setAudience] = useState('internal');

  // Arriving from "Request access" on the sign-in page lands on the form.
  useEffect(() => {
    if (hash) scrollTo(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    const previous = document.title;
    document.title = 'ActiveOptics: the AI CRM for component sales teams';
    return () => { document.title = previous; };
  }, []);

  const bullets = audience === 'internal' ? REVIEW_SAMPLE.internal : REVIEW_SAMPLE.customer;

  return (
    <div className="lp">
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-inner">
          <Link to="/" className="lp-brand" aria-label="ActiveOptics home">
            <BrandMark size={30} />
            <span>ActiveOptics</span>
          </Link>
          <nav className="lp-nav-links" aria-label="Page sections">
            <a href="#demo" onClick={(e) => { e.preventDefault(); scrollTo('demo'); }}>Demo</a>
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollTo('features'); }}>Features</a>
          </nav>
          <div className="lp-nav-actions">
            <button type="button" className="btn-icon" onClick={toggleTheme} aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <Link to="/login" className="lp-signin"><LogIn size={16} aria-hidden="true" /> Sign in</Link>
            <button type="button" className="btn btn-primary lp-nav-cta" onClick={() => scrollTo('request-access')}>Request access</button>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-wrap lp-hero-grid">
            <div className="lp-hero-copy">
              <span className="lp-eyebrow"><Sparkles size={14} aria-hidden="true" /> The AI CRM for component sales teams</span>
              <h1>Your meeting notes become your pipeline.</h1>
              <p className="lp-lede">
                Type or say what happened. ActiveOptics drafts the activity, the new contacts and the next steps,
                and you approve before anything is saved. Then it tells you what to do next.
              </p>
              <div className="lp-hero-ctas">
                <button type="button" className="btn btn-primary lp-btn-lg" onClick={() => scrollTo('demo')}>
                  Try the demo <ArrowRight size={18} aria-hidden="true" />
                </button>
                <button type="button" className="btn btn-secondary lp-btn-lg" onClick={() => scrollTo('request-access')}>
                  Request access
                </button>
              </div>
              <p className="lp-hero-foot">Built for teams that sell parts and modules direct and through rep firms.</p>
            </div>

            <div className="lp-window" aria-label="Sample of the Today page">
              <div className="lp-window-bar"><span /><span /><span /><em>Today</em></div>
              <div className="lp-window-body">
                {TODAY_SAMPLE.map(group => {
                  const Icon = TODAY_ICONS[group.section];
                  return (
                    <div key={group.section} className={`lp-today-group lp-tone-${group.tone}`}>
                      <div className="lp-today-head"><Icon size={14} aria-hidden="true" /> {group.section}</div>
                      {group.rows.map(row => (
                        <div key={row.title} className="lp-today-row">
                          <span className="lp-today-dot" aria-hidden="true" />
                          <span className="lp-today-title">{row.title}</span>
                          <span className="lp-today-meta">{row.meta}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
                <div className="lp-today-ai"><Sparkles size={14} aria-hidden="true" /> Draft a check-in for Cascade Networks</div>
              </div>
            </div>
          </div>
        </section>

        <section id="demo" className="lp-section lp-section--tint">
          <div className="lp-wrap">
            <div className="lp-section-head">
              <span className="lp-kicker">Try it now</span>
              <h2>From a rough note to clean records in one step</h2>
              <p>Pick a sample note and press Draft it. This demo uses sample data and saves nothing.</p>
            </div>
            <CaptureDemo />
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-wrap lp-split">
            <div>
              <span className="lp-kicker">Executive review</span>
              <h2>One account, told two ways</h2>
              <p>
                Each week ActiveOptics writes a few true bullets per key account from its own record.
                The internal version is candid about risks. The customer version leaves out risks, pricing and pipeline,
                so you can send it as it is. You refine either one in plain words, then download the PDF.
              </p>
            </div>
            <div className="lp-review">
              <div className="lp-review-toggle" role="tablist" aria-label="Audience">
                <button type="button" role="tab" aria-selected={audience === 'internal'} className={audience === 'internal' ? 'is-active' : ''} onClick={() => setAudience('internal')}>Internal</button>
                <button type="button" role="tab" aria-selected={audience === 'customer'} className={audience === 'customer' ? 'is-active' : ''} onClick={() => setAudience('customer')}>Customer-facing</button>
              </div>
              <div className="lp-review-card">
                <div className="lp-review-head">
                  <strong>{REVIEW_SAMPLE.account}</strong>
                  <span className="lp-pill lp-pill--ok">{REVIEW_SAMPLE.status}</span>
                </div>
                <ul>
                  {bullets.map(b => <li key={b}>{b}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="lp-section lp-section--tint">
          <div className="lp-wrap">
            <div className="lp-section-head">
              <span className="lp-kicker">Everything in one place</span>
              <h2>What your team runs on, every day</h2>
            </div>
            <div className="lp-features">
              {FEATURES.map(({ icon: Icon, title, text }) => (
                <div key={title} className="lp-feature">
                  <span className="lp-feature-icon"><Icon size={20} aria-hidden="true" /></span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-wrap">
            <div className="lp-section-head">
              <span className="lp-kicker">AI proposes, you approve</span>
              <h2>Built to be trusted with your accounts</h2>
            </div>
            <div className="lp-principles">
              {PRINCIPLES.map(({ icon: Icon, title, text }) => (
                <div key={title} className="lp-principle">
                  <Icon size={22} aria-hidden="true" />
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="request-access" className="lp-section lp-section--cta">
          <div className="lp-wrap lp-split lp-split--form">
            <div>
              <span className="lp-kicker">Request access</span>
              <h2>See it with your own accounts</h2>
              <p>Tell us a little about your team. We will reply to set up your access.</p>
            </div>
            <div className="lp-form-card">
              <RequestAccessForm />
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-wrap lp-footer-inner">
          <span className="lp-brand lp-brand--small"><BrandMark size={20} /> ActiveOptics</span>
          <span>Names in the demo and samples are fictional.</span>
          <Link to="/login">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
